import {
  ref,
  reactive,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
} from "vue";
import { useRoute } from "vue-router";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import acaoService from "@/service/acaoService";
import noticiaService from "@/service/noticiaService";
import { extrairTextoHtml } from "@/utils/FormatarEntradas";
import { getAtividadeEmCadastro } from "@/utils/atividadeCadastroStorage";

dayjs.extend(utc);
dayjs.extend(timezone);

export function useCadastroNoticia({ props = {}, emit = () => {} } = {}) {
  const route = useRoute();

  const atividade = ref({});
  const dadosAcao = ref(null);
  const abrangencias = ref([]);
  const autor = reactive({ id: null, nome: "" });
  const imagemSelecionada = ref(false);
  const mostrarErro = ref(false);
  const erroData = ref("");

  const form = ref({
    titulo: "",
    dataPublicacao: dayjs().format("YYYY-MM-DD"),
    texto: "",
    fonte: "",
    publicacaoImediata: true,
    descricaoImagem: "",
    palavrasChaves: [],
    abrangenciaId: null,
    imagemNome: "",
    imagem: {
      mimeType: "",
      base64Data: "",
      creditosImagem: "",
    },
  });

  const modal = reactive({
    visivel: false,
    mensagem: "",
    tipo: "", // 'publicada' | 'avaliacao' | 'erro'
    sucesso: true,
  });

  let acaoAoFecharModal = null;

  // Abrangência selecionada e verificações
  const abrangenciaSelecionada = computed(() => {
    return abrangencias.value.find((a) => a.id === form.value.abrangenciaId);
  });

  const abrangenciaNome = computed(() => {
    const a = abrangenciaSelecionada.value;
    return (a?.abrangenciaNoticia || a?.abrangencia || "").toLowerCase();
  });

  const isAcao = computed(() => {
    return (
      abrangenciaNome.value.includes("acao") ||
      abrangenciaNome.value.includes("ação")
    );
  });

  const isUnidadeOuPrex = computed(() => {
    return (
      abrangenciaNome.value.includes("unidade") ||
      abrangenciaNome.value.includes("prex")
    );
  });

  const isAgendamento = computed(() => {
    if (form.value.publicacaoImediata) return false;
    if (!form.value.dataPublicacao) return false;
    const hoje = dayjs().startOf("day");
    const dataPub = dayjs(form.value.dataPublicacao).startOf("day");
    return dataPub.isAfter(hoje);
  });

  const temTextoValido = computed(() => {
    return Boolean(form.value.texto && extrairTextoHtml(form.value.texto).trim());
  });

  const dataMinimaPublicacao = computed(() => {
    if (!form.value.publicacaoImediata) {
      if (isUnidadeOuPrex.value) {
        return dayjs().add(7, "day").format("YYYY-MM-DD");
      }
      return dayjs().add(1, "day").format("YYYY-MM-DD");
    }
    return dayjs().format("YYYY-MM-DD");
  });

  const dataMaximaPublicacao = computed(() => {
    if (
      atividade.value?.modalidadeInscricao === "NESCESSITA_INSCRICAO" &&
      atividade.value?.dataFimInscricao
    ) {
      return dayjs(atividade.value.dataFimInscricao).format("YYYY-MM-DD");
    }
    return undefined;
  });

  // Watchers para ajustar a data quando alternar tipo de publicação ou abrangência
  watch(
    () => form.value.publicacaoImediata,
    (novoTipo) => {
      erroData.value = "";
      if (novoTipo) {
        form.value.dataPublicacao = dayjs().format("YYYY-MM-DD");
      } else {
        if (isUnidadeOuPrex.value) {
          form.value.dataPublicacao = dayjs().add(7, "day").format("YYYY-MM-DD");
        } else {
          form.value.dataPublicacao = dayjs().add(1, "day").format("YYYY-MM-DD");
        }
      }
    }
  );

  watch(
    () => form.value.abrangenciaId,
    () => {
      if (!form.value.publicacaoImediata && isUnidadeOuPrex.value) {
        const hoje = dayjs().startOf("day");
        const dataAtual = dayjs(form.value.dataPublicacao).startOf("day");
        if (dataAtual.diff(hoje, "day") < 7) {
          form.value.dataPublicacao = dayjs().add(7, "day").format("YYYY-MM-DD");
        }
      }
    }
  );

  function abrirModal(tipo, mensagemCustomizada = "", sucesso = true) {
    modal.sucesso = sucesso;
    if (mensagemCustomizada) {
      modal.mensagem = mensagemCustomizada;
    } else if (tipo === "publicada") {
      modal.mensagem = isAgendamento.value
        ? `Notícia agendada com sucesso para ${dayjs(form.value.dataPublicacao).format("DD/MM/YYYY")}!`
        : "Notícia publicada com sucesso!";
    } else if (tipo === "avaliacao") {
      modal.mensagem = isAgendamento.value
        ? `Sua notícia foi enviada para avaliação e será publicada na data programada (${dayjs(form.value.dataPublicacao).format("DD/MM/YYYY")}) após a aprovação!`
        : "Sua notícia foi enviada para avaliação e será publicada imediatamente após a aprovação!";
    } else {
      modal.mensagem = mensagemCustomizada || "Operação realizada com sucesso!";
    }
    modal.tipo = tipo;
    modal.visivel = true;
  }

  function abrirAvisoErro(mensagem) {
    acaoAoFecharModal = null;
    modal.sucesso = false;
    modal.mensagem = mensagem;
    modal.tipo = "erro";
    modal.visivel = true;
  }

  function fecharModal() {
    modal.visivel = false;
    if (acaoAoFecharModal) {
      acaoAoFecharModal();
      acaoAoFecharModal = null;
    }
  }

  function processarImagem(imagem) {
    if (imagem) {
      form.value.imagem.mimeType = imagem.mimeType;
      form.value.imagem.base64Data = imagem.base64Data;
      imagemSelecionada.value = true;
    } else {
      form.value.imagem.mimeType = "";
      form.value.imagem.base64Data = "";
      form.value.imagemNome = "";
      form.value.descricaoImagem = "";
      imagemSelecionada.value = false;
    }
  }

  function toUTC(date) {
    if (!date) return null;
    const d = dayjs(date);
    if (!d.isValid()) return date;
    return d.tz("America/Sao_Paulo").utc().format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
  }

  function definirAcaoAoFecharModal(acao) {
    acaoAoFecharModal = acao;
  }

  function validarFormulario(isEdicao = false) {
    mostrarErro.value = false;
    erroData.value = "";

    if (!temTextoValido.value) {
      mostrarErro.value = true;
      abrirAvisoErro(
        "Não é possível cadastrar uma notícia sem o texto. Por favor, insira o conteúdo da notícia."
      );
      return { valido: false };
    }

    if (!form.value.palavrasChaves || form.value.palavrasChaves.length === 0) {
      mostrarErro.value = true;
      abrirAvisoErro(
        "Não é possível cadastrar uma notícia sem palavras-chave. Por favor, adicione pelo menos uma palavra-chave."
      );
      return { valido: false };
    }

    const temImagem = Boolean(
      form.value.imagem?.base64Data || imagemSelecionada.value
    );

    if (!isEdicao) {
      if (!form.value.titulo?.trim()) {
        mostrarErro.value = true;
        abrirAvisoErro("Por favor, informe o título da notícia.");
        return { valido: false };
      }
      if (!form.value.fonte?.trim()) {
        mostrarErro.value = true;
        abrirAvisoErro("Por favor, informe a fonte da notícia.");
        return { valido: false };
      }
      if (!form.value.abrangenciaId) {
        mostrarErro.value = true;
        abrirAvisoErro("Por favor, selecione a abrangência da notícia.");
        return { valido: false };
      }
      if (temImagem && !form.value.descricaoImagem?.trim()) {
        mostrarErro.value = true;
        abrirAvisoErro(
          "Por favor, informe a descrição da imagem para acessibilidade."
        );
        return { valido: false };
      }
  
      const hoje = dayjs().startOf("day");
  
      if (form.value.publicacaoImediata) {
        // Publicação imediata: a data é a data atual
        form.value.dataPublicacao = dayjs().format("YYYY-MM-DD");
      } else {
        // Publicação programada: validação da data informada
        if (!form.value.dataPublicacao) {
          erroData.value = "Informe a data de publicação programada.";
          abrirAvisoErro(erroData.value);
          return { valido: false };
        }

        const dataPub = dayjs(form.value.dataPublicacao).startOf("day");

        if (dataPub.isBefore(hoje) || dataPub.isSame(hoje)) {
          erroData.value =
            "Para publicação programada, a data de publicação deve ser posterior à data de hoje.";
          abrirAvisoErro(erroData.value);
          return { valido: false };
        }

        // Se for UNIDADE ou PREX, no mínimo uma semana de antecedência
        if (isUnidadeOuPrex.value) {
          const diffDays = dataPub.diff(hoje, "day");
          if (diffDays < 7) {
            erroData.value =
              "Para publicação programada com abrangência UNIDADE ou PREX, a data de publicação deve ter no mínimo uma semana (7 dias) de diferença em relação à data atual para que possa ser avaliada.";
            abrirAvisoErro(erroData.value);
            return { valido: false };
          }
        }

        // Verificação da data máxima em relação a inscrições de atividade
        if (
          atividade.value?.modalidadeInscricao === "NESCESSITA_INSCRICAO" &&
          atividade.value?.dataFimInscricao
        ) {
          const dataFimInscricao = dayjs(atividade.value.dataFimInscricao).endOf(
            "day"
          );
          if (dataPub.isAfter(dataFimInscricao)) {
            erroData.value =
              "Não é permitido programar uma notícia com data de publicação posterior à data de término de inscrição da atividade.";
            abrirAvisoErro(erroData.value);
            return { valido: false };
          }
        }
      }
    }

    const acaoId = route.params.id || props.acaoId;

    if (!acaoId) {
      abrirAvisoErro(
        "Erro: ID da ação não encontrado. Certifique-se de que está acessando a página corretamente."
      );
      return { valido: false };
    }

    if (!autor.id) {
      abrirAvisoErro(
        "Erro: Não foi possível identificar o autor da notícia. Verifique sua conexão ou tente recarregar a página."
      );
      return { valido: false };
    }

    if (!abrangenciaSelecionada.value && abrangencias.value.length === 0) {
      abrirAvisoErro(
        "Erro: As opções de abrangência não foram carregadas. Tente recarregar a página."
      );
      return { valido: false };
    }

    const tipoModal = isAcao.value ? "publicada" : "avaliacao";

    const payloadNoticia = {
      id: form.value.id || null,
      titulo: form.value.titulo,
      texto: form.value.texto,
      fonte: form.value.fonte,
      palavrasChaves: [...form.value.palavrasChaves],
      dataAutorizacao: toUTC(new Date()),
      autorId: autor.id,
      publicacaoImediata: form.value.publicacaoImediata,
      imagem:
      form.value.imagem.base64Data !== ""
      ? {
        mimeType: form.value.imagem.mimeType || "image/png",
        base64Data: form.value.imagem.base64Data,
        creditosImagem: form.value.imagem.creditosImagem || "",
      }
      : null,
      descricaoImagem: form.value.descricaoImagem || "",

      ...(isEdicao ? {} : {
        abrangenciaId: form.value.abrangenciaId || abrangencias.value[0]?.id,
        dataPublicacao:
          form.value.publicacaoImediata
            ? toUTC(new Date())
            : toUTC(form.value.dataPublicacao),
      })
    };

    const isAtividade = Boolean(atividade.value?.nome);
    let payloadAtividade = null;
    let targetAcaoId = acaoId;
    let msgSucesso = "";

    if (isAtividade) {
      const atividadeObj = atividade.value;
      targetAcaoId = acaoId || atividadeObj.acaoExtensaoId;

      payloadAtividade = {
        nome: atividadeObj.nome,
        descricao: atividadeObj.descricao,
        subtipo_id: Number(atividadeObj.subtipo_id),
        modalidadeExecucao: atividadeObj.modalidadeExecucao,
        modalidadeInscricao: atividadeObj.modalidadeInscricao,
        local: atividadeObj.local,
        localLabel: atividadeObj.localLabel || null,
        acaoExtensaoId: targetAcaoId,
        dataInicioInscricao:
          atividadeObj.modalidadeInscricao === "NESCESSITA_INSCRICAO" &&
          atividadeObj.dataInicioInscricao
            ? toUTC(atividadeObj.dataInicioInscricao)
            : null,
        dataFimInscricao:
          atividadeObj.modalidadeInscricao === "NESCESSITA_INSCRICAO" &&
          atividadeObj.dataFimInscricao
            ? toUTC(atividadeObj.dataFimInscricao)
            : null,
        criterioInscricao:
          atividadeObj.modalidadeInscricao === "NESCESSITA_INSCRICAO"
            ? atividadeObj.criterioInscricao
            : null,
        numeroDeVagasOfertadas:
          atividadeObj.modalidadeInscricao === "NESCESSITA_INSCRICAO"
            ? Number(atividadeObj.numeroDeVagasOfertadas) || 0
            : null,
        linkFormulario: atividadeObj.linkFormulario || "",
        criarNoticia: true,
        objetivos: Array.isArray(atividadeObj.objetivos)
          ? [...atividadeObj.objetivos]
          : [],
        encontros: Array.isArray(atividadeObj.encontros)
          ? atividadeObj.encontros.map((e) => ({
              titulo: e.titulo,
              descricao: e.descricao,
              inicio_periodo: toUTC(e.inicio_periodo),
              fim_periodo: toUTC(e.fim_periodo),
            }))
          : [],
        noticia: payloadNoticia,
        imagem: atividadeObj.imagem?.base64Data
          ? {
              mimeType: atividadeObj.imagem.mimeType || "image/png",
              base64Data: atividadeObj.imagem.base64Data,
              creditosImagem: atividadeObj.imagem.creditosImagem || "",
            }
          : null,
        palavrasChave: Array.isArray(atividadeObj.palavrasChave)
          ? [...atividadeObj.palavrasChave]
          : [],
        latitude:
          atividadeObj.latitude !== null &&
          atividadeObj.latitude !== undefined &&
          atividadeObj.latitude !== ""
            ? Number(Number(atividadeObj.latitude).toFixed(6))
            : null,
        longitude:
          atividadeObj.longitude !== null &&
          atividadeObj.longitude !== undefined &&
          atividadeObj.longitude !== ""
            ? Number(Number(atividadeObj.longitude).toFixed(6))
            : null,
        dataInicioRealizacao:
          (!atividadeObj.encontros || atividadeObj.encontros.length === 0) &&
          atividadeObj.dataInicioRealizacao
            ? toUTC(atividadeObj.dataInicioRealizacao)
            : null,
        dataFimRealizacao:
          (!atividadeObj.encontros || atividadeObj.encontros.length === 0) &&
          atividadeObj.dataFimRealizacao
            ? toUTC(atividadeObj.dataFimRealizacao)
            : null,
      };

      msgSucesso =
        !form.value.publicacaoImediata
          ? `Notícia agendada para ${dayjs(form.value.dataPublicacao).format("DD/MM/YYYY")} e atividade cadastradas com sucesso!`
          : "Notícia e atividade cadastradas com sucesso!";
    }

    return {
      valido: true,
      isAtividade,
      payloadNoticia,
      payloadAtividade,
      acaoId,
      targetAcaoId,
      tipoModal,
      msgSucesso,
    };
  }

  async function carregarAcao() {
    const acaoId = route.params.id || props.acaoId;
    if (acaoId) {
      try {
        dadosAcao.value = await acaoService.getAcoesById(acaoId);
      } catch (error) {
        console.error("Erro ao carregar dados da ação:", error);
      }
    }
  }

  async function getAutor() {
    try {
      const response = await noticiaService.getAllAutorNoticia();
      if (response && response.length > 0) {
        autor.id = response[0].id;
        autor.nome = response[0].autoNoticia;
      }
    } catch (error) {
      console.error("Erro ao buscar autor:", error);
    }
  }

  async function buscarAbrangecias() {
    abrangencias.value = await noticiaService.getAllAbrangenciasNoticia();
    if (abrangencias.value && abrangencias.value.length > 0) {
      const abrangenciaAcao = abrangencias.value.find((a) => {
        const nome = (
          a.abrangenciaNoticia ||
          a.abrangencia ||
          ""
        ).toLowerCase();
        return nome.includes("acao") || nome.includes("ação");
      });
      if (!form.value.abrangenciaId) {
        form.value.abrangenciaId = abrangenciaAcao
          ? abrangenciaAcao.id
          : abrangencias.value[0]?.id;
      }
    }
  }

  function gerarTextoBaseNoticia(atv, acao) {
    const nomeAcao = acao?.titulo || "<Ação de extensão>";
    const nomeAtividade = atv?.nome || "<Título da Atividade>";
    const tipoAtividade =
      atv?.subtipoNome ||
      atv?.tipoNome ||
      "<Tipo da Atividade: Minicurso/Evento/Oficina/Roda de Conversa>";
    const temaPrincipal = "<Breve Destaque do Tema Principal>";

    let dataPrimeiroEncontro = "<Data do primeiro encontro>";
    if (atv?.encontros?.length > 0 && atv.encontros[0]?.inicio_periodo) {
      dataPrimeiroEncontro = dayjs(atv.encontros[0].inicio_periodo).format(
        "DD/MM/YYYY"
      );
    } else if (atv?.dataInicioRealizacao) {
      dataPrimeiroEncontro = dayjs(atv.dataInicioRealizacao).format(
        "DD/MM/YYYY"
      );
    }

    const localAtividade =
      atv?.localLabel || atv?.local || "<Localização da atividade>";
    const publicoAlvo = "<Público-Alvo, se houver>";

    const descricaoAtividade =
      atv?.descricao && atv.descricao.trim()
        ? atv.descricao.trim()
        : `<Descrição detalhada – Explique o propósito, conteúdo e relevância. Exemplo: "Com foco em <Objetivo Relacionado>, a atividade promoverá <exemplos de dinâmicas, palestras ou resultados esperados>. Os participantes terão a oportunidade de <benefícios ou aprendizados>.">`;

    let modalidadeTexto = "";
    if (atv?.modalidadeInscricao === "NESCESSITA_INSCRICAO") {
      const dataInicio = atv?.dataInicioInscricao
        ? dayjs(atv.dataInicioInscricao).format("DD/MM/YYYY")
        : "<Data de Início>";
      const dataFim = atv?.dataFimInscricao
        ? dayjs(atv.dataFimInscricao).format("DD/MM/YYYY")
        : "<Data de Término>";

      let vagas = "<Número de Vagas>";
      if (
        atv?.numeroDeVagasOfertadas !== null &&
        atv?.numeroDeVagasOfertadas !== undefined &&
        atv?.numeroDeVagasOfertadas !== ""
      ) {
        vagas =
          Number(atv.numeroDeVagasOfertadas) === 0
            ? "Ilimitadas"
            : String(atv.numeroDeVagasOfertadas);
      }

      const criterio =
        atv?.criterioInscricao === "ORDEMDEINSCRICAO"
          ? "Ordem de Inscrição"
          : atv?.criterioInscricao === "CRITERIODACOORDENACAO"
            ? "Decisão da Coordenação"
            : "<Ordem de Inscrição/Decisão da Coordenação>";

      const linkForm = atv?.linkFormulario || "<Link para Formulário>";

      modalidadeTexto = `Com inscrição: As inscrições estão abertas de ${dataInicio} até ${dataFim}.

        Vagas: ${vagas} (preenchidas por ${criterio}).

         Caso haja formulário seletivo: Os interessados devem preencher o formulário disponível em ${linkForm}.`;
    } else if (
      atv?.modalidadeInscricao === "NAO_NESCESSITA_INSCRICAO_COM_FREQUENCIA"
    ) {
      modalidadeTexto = `Sem inscrição, mas com frequência registrada: Participação livre e gratuita! Basta comparecer no local e horário indicados.`;
    } else if (
      atv?.modalidadeInscricao === "NAO_NESCESSITA_INSCRICAO_SEM_FREQUENCIA"
    ) {
      modalidadeTexto = `Sem inscrição e sem frequência: Evento aberto – não há registro de participação.`;
    } else {
      modalidadeTexto = `Com inscrição: As inscrições estão abertas de <Data de Início> até <Data de Término>.

        Vagas: <Número de Vagas> (preenchidas por <Ordem de Inscrição/Decisão da Coordenação>).

         Caso haja formulário seletivo: Os interessados devem preencher o formulário disponível em <Link para Formulário>.

Sem inscrição, mas com frequência registrada: Participação livre e gratuita! Basta comparecer no local e horário indicados.

Sem inscrição e sem frequência: Evento aberto – não há registro de participação.`;
    }

    const localCompleto =
      atv?.localLabel ||
      atv?.local ||
      "<Localização Completa + Link para mapa, se aplicável>";
    const emailCoord =
      acao?.emailCoordenador ||
      acao?.coordenador?.email ||
      acao?.email ||
      "<Email do coordenador extensionista>";

    return `O ${nomeAcao} tem o prazer de anunciar a atividade ${nomeAtividade}, que será um(a) ${tipoAtividade} que abordará ${temaPrincipal}. A ação será realizada no dia ${dataPrimeiroEncontro}, no ${localAtividade}, e é voltada para ${publicoAlvo}.

Sobre a Atividade:
${descricaoAtividade}

Modalidade de Participação:

${modalidadeTexto}

Informações Adicionais:

Local: ${localCompleto}

Não perca esta oportunidade! Para dúvidas, entre em contato com ${emailCoord}.`;
  }

  onMounted(async () => {
    const atividadeEmMemoriaOuStorage = getAtividadeEmCadastro();

    if (atividadeEmMemoriaOuStorage) {
      atividade.value = atividadeEmMemoriaOuStorage;
    } else if (props.atividadeObjeto) {
      atividade.value = props.atividadeObjeto;
    } else if (window.history.state?.atividadeObjeto) {
      atividade.value = window.history.state.atividadeObjeto;
    }

    await carregarAcao();
    await buscarAbrangecias();
    await getAutor();

    if (atividade.value?.nome) {
      if (!form.value.titulo) {
        form.value.titulo = atividade.value.nome;
      }
      if (
        form.value.palavrasChaves.length === 0 &&
        Array.isArray(atividade.value.palavrasChave)
      ) {
        form.value.palavrasChaves = [...atividade.value.palavrasChave];
      }
      if (!form.value.texto) {
        form.value.texto = gerarTextoBaseNoticia(atividade.value, dadosAcao.value);
      }

      if (atividade.value.imagem?.base64Data && !form.value.imagem.base64Data) {
        form.value.imagem = {
          mimeType: atividade.value.imagem.mimeType || "image/png",
          base64Data: atividade.value.imagem.base64Data,
          creditosImagem: atividade.value.imagem.creditosImagem || "",
        };
        if (!form.value.descricaoImagem) {
          form.value.descricaoImagem =
            atividade.value.imagem.descricaoImagem ||
            `Imagem da atividade ${atividade.value.nome || ""}`.trim();
        }
        form.value.imagemNome = "Imagem da Atividade";
        imagemSelecionada.value = true;
      } else if (!form.value.imagem.base64Data && dadosAcao.value?.imagemUrl) {
        form.value.imagem = {
          url: dadosAcao.value.imagemUrl,
          mimeType: "",
          base64Data: "",
          creditosImagem: "",
        };
        if (!form.value.descricaoImagem) {
          form.value.descricaoImagem =
            dadosAcao.value.descricaoImagem ||
            `Imagem da ação ${dadosAcao.value.titulo || ""}`;
        }
        form.value.imagemNome = "Imagem da Ação";
        imagemSelecionada.value = true;
      }
    } else if (!form.value.imagem.base64Data && dadosAcao.value?.imagemUrl) {
      form.value.imagem = {
        url: dadosAcao.value.imagemUrl,
        mimeType: "",
        base64Data: "",
        creditosImagem: "",
      };
      if (!form.value.descricaoImagem) {
        form.value.descricaoImagem =
          dadosAcao.value.descricaoImagem ||
          `Imagem da ação ${dadosAcao.value.titulo || ""}`;
      }
      form.value.imagemNome = "Imagem da Ação";
      imagemSelecionada.value = true;
    }

    if (dadosAcao.value?.titulo && !form.value.fonte) {
      form.value.fonte = dadosAcao.value.titulo;
    }
  });

  onBeforeUnmount(() => {
    const layoutElement = document.getElementById("layout");
    if (layoutElement) layoutElement.classList.remove("layout-com-cadastro");
  });

  return {
    form,
    atividade,
    modal,
    abrangencias,
    abrangenciaSelecionada,
    isAcao,
    isUnidadeOuPrex,
    isAgendamento,
    dataMinimaPublicacao,
    dataMaximaPublicacao,
    mostrarErro,
    erroData,
    temTextoValido,
    imagemSelecionada,
    processarImagem,
    validarFormulario,
    definirAcaoAoFecharModal,
    abrirModal,
    abrirAvisoErro,
    fecharModal,
    buscarAbrangecias,
  };
}
